// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useRef } from 'react';
import { DndContext, DragOverlay, TraversalOrder } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { Portal } from '@cloudscape-design/component-toolkit/internal';
import { fireNonCancelableEvent } from '../../events';
import { isElement, isHTMLElement } from '../../utils/dom';
import { getFirstScrollableParent } from '../../utils/scrollable-containers';
import { joinStrings } from '../../utils/strings';
import { EventName } from './keyboard-sensor/utilities/events';
import useDragAndDropReorder from './use-drag-and-drop-reorder';
import useLiveAnnouncements from './use-live-announcements';
import styles from './styles.css.js';
export default function SortableArea({ items, itemDefinition, renderItem, onItemsChange, disableReorder, i18nStrings, }) {
    var _a;
    const { activeItemId, setActiveItemId, collisionDetection, handleKeyDown, sensors, isKeyboard } = useDragAndDropReorder({
        items,
        itemDefinition,
    });
    const activeItem = activeItemId ? items.find(item => itemDefinition.id(item) === activeItemId) : null;
    const isDragging = activeItemId !== null;
    const announcements = useLiveAnnouncements({ items, itemDefinition, isDragging, ...i18nStrings });
    const portalContainer = usePortalContainer();
    const { autoScroll, setAutoScrollBoundary } = useScopedAutoScroll();
    return (React.createElement(DndContext, { sensors: sensors, collisionDetection: collisionDetection, autoScroll: autoScroll, accessibility: {
            announcements,
            restoreFocus: false,
            screenReaderInstructions: (i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.dragHandleAriaDescription)
                ? { draggable: i18nStrings.dragHandleAriaDescription }
                : undefined,
            container: portalContainer !== null && portalContainer !== void 0 ? portalContainer : undefined,
        }, onDragStart: ({ active, activatorEvent }) => {
            setAutoScrollBoundary(activatorEvent.target);
            setActiveItemId(active.id);
        }, onDragEnd: event => {
            setAutoScrollBoundary(null);
            setActiveItemId(null);
            const { active, over } = event;
            if (over && active.id !== over.id) {
                const movedItem = items.find(item => itemDefinition.id(item) === active.id);
                const oldIndex = items.findIndex(item => itemDefinition.id(item) === active.id);
                const newIndex = items.findIndex(item => itemDefinition.id(item) === over.id);
                fireNonCancelableEvent(onItemsChange, { items: arrayMove([...items], oldIndex, newIndex), movedItem });
            }
        }, onDragCancel: () => {
            setAutoScrollBoundary(null);
            setActiveItemId(null);
        } },
        React.createElement(SortableContext, { disabled: disableReorder, items: items.map(item => itemDefinition.id(item)), strategy: verticalListSortingStrategy }, items.map(item => (React.createElement(DraggableItem, { key: itemDefinition.id(item), item: item, itemDefinition: itemDefinition, showDirectionButtons: item === activeItem && isKeyboard.current, renderItem: renderItem, onKeyDown: handleKeyDown, dragHandleAriaLabel: i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.dragHandleAriaLabel })))),
        React.createElement(Portal, { container: portalContainer },
            React.createElement(DragOverlay, { className: clsx(styles['drag-overlay'], styles[`drag-overlay-${getBorderRadiusVariant(itemDefinition)}`]), dropAnimation: null, style: { zIndex: 5000 }, transition: isKeyboard.current ? 'transform 250ms' : '' }, activeItem &&
                renderItem({
                    item: activeItem,
                    id: activeItemId.toString(),
                    style: {},
                    className: styles.active,
                    isDropPlaceholder: true,
                    isSortingActive: false,
                    isDragGhost: true,
                    dragHandleProps: {
                        ariaLabel: (_a = joinStrings(i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.dragHandleAriaLabel, itemDefinition.label(activeItem))) !== null && _a !== void 0 ? _a : '',
                        active: true,
                        onKeyDown: handleKeyDown,
                    },
                })))));
}
// dnd-kit auto-scrolls every scrollable ancestor up to the first fixed-position element, outermost first by default
// (https://github.com/clauderic/dnd-kit/issues/1893). Restrict it to the sortable area's nearest scroll container so
// that dragging inside e.g. a sticky drawer does not scroll unrelated outer containers. Without a scrollable ancestor,
// all containers (including the window) remain eligible.
function useScopedAutoScroll() {
    const boundaryRef = useRef(null);
    const autoScroll = {
        order: TraversalOrder.ReversedTreeOrder,
        canScroll: element => !boundaryRef.current || boundaryRef.current.contains(element),
    };
    const setAutoScrollBoundary = (dragStartTarget) => {
        var _a;
        let element = isElement(dragStartTarget) ? dragStartTarget : null;
        while (element && !isHTMLElement(element)) {
            element = element.parentElement;
        }
        boundaryRef.current = element ? ((_a = getFirstScrollableParent(element)) !== null && _a !== void 0 ? _a : null) : null;
    };
    return { autoScroll, setAutoScrollBoundary };
}
function usePortalContainer() {
    const portalContainerRef = useRef(typeof document !== 'undefined' ? document.createElement('div') : null);
    useEffect(() => {
        const container = portalContainerRef.current;
        if (container && !container.isConnected) {
            document.body.appendChild(container);
        }
        return () => {
            if (container && container.isConnected) {
                document.body.removeChild(container);
            }
        };
    }, []);
    return portalContainerRef.current;
}
function DraggableItem({ item, itemDefinition, dragHandleAriaLabel, showDirectionButtons, onKeyDown, renderItem, }) {
    var _a;
    const id = itemDefinition.id(item);
    const { isDragging, isSorting, listeners, setNodeRef, transform, attributes } = useSortable({
        id,
    });
    const style = { transform: CSS.Translate.toString(transform) };
    const dragHandleListeners = attributes['aria-disabled']
        ? {}
        : {
            ...listeners,
            onKeyDown: (event) => {
                if (onKeyDown) {
                    onKeyDown(event);
                }
                if (listeners === null || listeners === void 0 ? void 0 : listeners.onKeyDown) {
                    listeners.onKeyDown(event);
                }
            },
        };
    const className = clsx(isDragging && clsx(styles.placeholder, styles[`placeholder-${getBorderRadiusVariant(itemDefinition)}`]), isSorting && styles.sorting);
    const dragHandleRef = useRef(null);
    return (React.createElement(React.Fragment, null, renderItem({
        item,
        id,
        ref: setNodeRef,
        style,
        className,
        isDropPlaceholder: isDragging,
        isSortingActive: isSorting,
        isDragGhost: false,
        dragHandleProps: {
            ...dragHandleListeners,
            active: isDragging,
            ariaLabel: (_a = joinStrings(dragHandleAriaLabel, itemDefinition.label(item))) !== null && _a !== void 0 ? _a : '',
            ariaDescribedby: attributes['aria-describedby'],
            disabled: attributes['aria-disabled'],
            triggerMode: 'controlled',
            controlledShowButtons: showDirectionButtons,
            ref: dragHandleRef,
            directions: showDirectionButtons
                ? {
                    'block-start': 'active',
                    'block-end': 'active',
                }
                : undefined,
            onDirectionClick: direction => {
                var _a;
                const event = new Event(direction === 'block-start' ? EventName.CustomUp : EventName.CustomDown, {
                    bubbles: true,
                    cancelable: true,
                });
                onKeyDown(event);
                (_a = dragHandleRef.current) === null || _a === void 0 ? void 0 : _a.dispatchEvent(event);
            },
        },
    })));
}
export function getBorderRadiusVariant(itemDefinition) {
    var _a;
    return (_a = itemDefinition.borderRadius) !== null && _a !== void 0 ? _a : 'item';
}
//# sourceMappingURL=index.js.map